if (!customElements.get('main-collection-tabs')) {
  customElements.define(
    'main-collection-tabs',
    class MainCollectionTabs extends HTMLElement {
      connectedCallback() {
        this.abortController = null;
        this.addEventListener('click', this);
        window.addEventListener('popstate', this);
        this.scrollActiveTab('auto');
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.removeEventListener('click', this);
        window.removeEventListener('popstate', this);
      }

      handleEvent(event) {
        if (event.type === 'popstate') {
          const tab = this.querySelector(`[data-tab-index="${event.state?.tabIndex}"]`);

          if (tab) {
            this.activateTab(tab);
          }

          this.load(window.location.href, { updateHistory: false, tabIndex: event.state?.tabIndex });
          return;
        }

        const tab = event.target instanceof Element ? event.target.closest('.main-collection__tab') : null;

        if (!tab || tab.classList.contains('is-active') || !this.contains(tab)) {
          return;
        }

        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }

        event.preventDefault();
        this.activateTab(tab);
        this.load(tab.href, { tabIndex: tab.dataset.tabIndex });
      }

      async load(url, options = {}) {
        const section = this.closest('.shopify-section--main-collection');
        const sectionId = section?.id?.replace('shopify-section-', '');
        const currentContent = section?.querySelector('[data-collection-content]');

        if (!section || !sectionId || !currentContent) {
          window.location.href = url;
          return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();
        section.classList.add('is-loading-tabs');

        try {
          const response = await fetch(this.sectionUrl(url, sectionId), { signal: this.abortController.signal });

          if (!response.ok) {
            throw new Error(`Section request failed: ${response.status}`);
          }

          const nextSection = new DOMParser()
            .parseFromString(await response.text(), 'text/html')
            .querySelector('.shopify-section--main-collection');
          const nextContent = nextSection?.querySelector('[data-collection-content]');

          if (!nextContent) {
            throw new Error('Collection content missing in response');
          }

          [
            '[data-collection-mobile-facets]',
            'facet-drawer',
            '.collection__top-bar',
            'facet-floating-filter',
            '.collection__facets',
          ].forEach((selector) => this.replacePart(section, nextSection, selector));

          currentContent.replaceWith(nextContent);
          this.replacePart(section, nextSection, '.collection__pagination', '.collection__results');
          section.classList.remove('is-loading-tabs');
          section.classList.add('is-entering-tabs');

          requestAnimationFrame(() => {
            section.classList.remove('is-entering-tabs');
          });

          if (options.updateHistory !== false) {
            history.pushState({ tabIndex: options.tabIndex }, '', url);
          }

          document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { section } }));
        } catch (error) {
          section.classList.remove('is-loading-tabs', 'is-entering-tabs');

          if (error.name !== 'AbortError') {
            window.location.href = url;
          }
        }
      }

      activateTab(activeTab) {
        this.querySelectorAll('.main-collection__tab').forEach((tab) => {
          const isActive = tab === activeTab;
          tab.classList.toggle('is-active', isActive);

          if (isActive) {
            tab.setAttribute('aria-current', 'page');
          } else {
            tab.removeAttribute('aria-current');
          }
        });

        const heading = this.querySelector('.main-collection__heading');

        if (heading) {
          heading.textContent = activeTab.textContent.trim();
        }

        this.scrollActiveTab('smooth');
      }

      replacePart(section, nextSection, selector, fallbackSelector = null) {
        const currentPart = section.querySelector(selector);
        const nextPart = nextSection?.querySelector(selector);

        if (currentPart && nextPart) {
          currentPart.replaceWith(nextPart);
        } else if (currentPart) {
          currentPart.remove();
        } else if (nextPart && fallbackSelector) {
          section.querySelector(fallbackSelector)?.after(nextPart);
        }
      }

      sectionUrl(url, sectionId) {
        const sectionUrl = new URL(url, window.location.origin);
        sectionUrl.searchParams.set('section_id', sectionId);
        return sectionUrl;
      }

      scrollActiveTab(behavior) {
        const viewport = this.querySelector('.main-collection__tabs-viewport');
        const activeTab = this.querySelector('.main-collection__tab.is-active');

        if (!viewport || !activeTab || window.matchMedia('(min-width: 768px)').matches) {
          return;
        }

        activeTab.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior,
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  );
}
